import { Button, ColorField, Skeleton } from '@platform/design-system';
import { ImageIcon, RotateCw, X } from 'lucide-react';
import { useState } from 'react';
import { useTenantDetail } from '../catalog/useCatalogQueries.ts';
import { FileDropZone } from '../FileDropZone.tsx';
import { LoadFailed } from '../LoadFailed.tsx';
import { acceptOf, imageFiles } from '../media/media.ts';
import { useImageUploads } from '../media/useImageUploads.ts';
import { usePageContext } from '../usePageContext.ts';
import {
  BRAND_COLOR_ROLES,
  DEFAULT_BRAND_COLORS,
  useBranding,
  type BrandColors,
  type Branding,
} from './branding.ts';
import { LabelPreview } from './LabelPreview.tsx';
import type { StepViewProps } from './step-flow.ts';
import type { TenantView } from './store-profile.ts';
import styles from './Setup.module.css';

const BRAND_LOGO = 'brand_logo';

type LogoChoice = { readonly mediaId: string; readonly url: string | null } | null;

const logoChoiceOf = (branding: Branding): LogoChoice =>
  branding.logo === null ? null : { mediaId: branding.logo.mediaId, url: branding.logo.urls.w320 };

type BrandFormProps = StepViewProps & { branding: Branding; tenant: TenantView };

const BrandForm = ({ api, flow, branding, tenant }: BrandFormProps) => {
  const { t, invalidate } = usePageContext();
  const [logo, setLogo] = useState<LogoChoice>(logoChoiceOf(branding));
  const [colors, setColors] = useState<BrandColors>(branding.colors ?? DEFAULT_BRAND_COLORS);
  const uploads = useImageUploads(api, BRAND_LOGO, (view) => {
    setLogo({ mediaId: view.id, url: view.urls?.w320 ?? null });
  });
  const uploading = uploads.pending.find((entry) => entry.phase !== 'failed');
  const failed = uploads.pending.find((entry) => entry.phase === 'failed');
  const shownLogo = uploading?.preview ?? logo?.url ?? null;

  const receive = (file: File) => {
    const { accepted } = imageFiles(BRAND_LOGO, [file]);
    if (accepted.length === 0) {
      flow.fail(t('setup.logoRefused', { name: file.name }));
      return;
    }
    for (const entry of uploads.pending) uploads.dismiss(entry.key);
    uploads.add(accepted);
  };

  return (
    <form
      id={flow.formId}
      className={styles['brand']}
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        if (uploading !== undefined) {
          flow.fail(t('setup.logoBusy'));
          return;
        }
        flow.submit(async () => {
          await api.put('/admin/tenant/branding', {
            body: { logoMediaId: logo?.mediaId ?? null, colors, version: branding.version },
          });
          await invalidate('branding');
        });
      }}
    >
      <div className={styles['stack']}>
        <fieldset className={styles['fieldset']}>
          <legend className={styles['legend']}>{t('setup.logo')}</legend>
          <div className={styles['logoRow']}>
            <span className={styles['logoBox']}>
              {shownLogo === null ? (
                <ImageIcon size={24} aria-hidden="true" />
              ) : (
                <img src={shownLogo} alt={t('setup.logoAlt')} />
              )}
            </span>
            <div className={styles['logoState']} aria-live="polite">
              {uploading === undefined ? null : (
                <span className={styles['muted']}>{t('setup.logoUploading')}</span>
              )}
              {failed === undefined ? null : (
                <>
                  <span className={styles['danger']}>{failed.error}</span>
                  <Button
                    size="sm"
                    variant="outline"
                    icon={<RotateCw size={16} aria-hidden="true" />}
                    onClick={() => {
                      uploads.retry(failed.key);
                    }}
                  >
                    {t('common.retry')}
                  </Button>
                </>
              )}
              {logo === null || uploading !== undefined ? null : (
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<X size={16} aria-hidden="true" />}
                  onClick={() => {
                    setLogo(null);
                  }}
                >
                  {t('setup.logoRemove')}
                </Button>
              )}
            </div>
          </div>
          <FileDropZone
            accept={acceptOf(BRAND_LOGO)}
            label={t('setup.logoDrop')}
            helperText={t('setup.logoHelp')}
            onFile={receive}
          />
        </fieldset>
        <fieldset className={styles['fieldset']}>
          <legend className={styles['legend']}>{t('setup.colors')}</legend>
          <div className={styles['colors']}>
            {BRAND_COLOR_ROLES.map((role) => (
              <ColorField
                key={role}
                label={t(`setup.color_${role}`)}
                value={colors[role]}
                onChange={(value) => {
                  setColors((current) => ({ ...current, [role]: value }));
                }}
                readableHint={t('setup.colorReadable')}
                lowContrastHint={t('setup.colorLowContrast')}
                invalidHint={t('setup.colorInvalid')}
                sample={t('setup.colorSample')}
              />
            ))}
          </div>
        </fieldset>
      </div>
      <LabelPreview shopName={tenant.name} currency={tenant.currency} logoUrl={shownLogo} colors={colors} />
    </form>
  );
};

export const BrandStep = (props: StepViewProps) => {
  const { t, describeError } = usePageContext();
  const branding = useBranding(props.api);
  const tenant = useTenantDetail(props.api);
  const failed = [branding, tenant].find((query) => query.isError);
  if (failed !== undefined)
    return <LoadFailed description={describeError(failed.error)} onRetry={() => void failed.refetch()} />;
  if (branding.data === undefined || tenant.data === undefined)
    return <Skeleton height="420px" label={t('setup.brandTitle')} />;
  return <BrandForm key={branding.data.version} {...props} branding={branding.data} tenant={tenant.data} />;
};
